// Pure-function balance + ledger math. Used by the UI to render the
// per-family ledger without round-tripping for every recomputation, and
// by tests to verify the SQL view returns matching numbers.

import type { ChargeStatus } from './types';

export interface ChargeView {
  id: string;
  amount_cents: number;
  status: ChargeStatus;
  due_date: string | null;
  allocated_cents?: number; // optional; defaults to 0 when not set
}

export interface BalanceSummary {
  outstandingCents: number;
  openCount: number;
  overdueCount: number;
  pastDueCents: number;
  creditCents: number; // negative-amount charges add to credit
}

export function computeFamilyBalance(
  charges: ChargeView[],
  asOf: Date = new Date(),
): BalanceSummary {
  const today = asOf.toISOString().slice(0, 10);
  const summary: BalanceSummary = {
    outstandingCents: 0,
    openCount: 0,
    overdueCount: 0,
    pastDueCents: 0,
    creditCents: 0,
  };
  for (const c of charges) {
    if (c.status === 'void' || c.status === 'written_off') continue;
    const allocated = c.allocated_cents ?? 0;
    const remaining = c.amount_cents - allocated;
    if (c.amount_cents < 0) {
      // Credits offset future outstanding. Tally as creditCents so the UI
      // can show "$X credit available" alongside the balance.
      summary.creditCents += -c.amount_cents;
    }
    if (c.status === 'pending' || c.status === 'partial') {
      if (remaining > 0) {
        summary.outstandingCents += remaining;
        summary.openCount += 1;
        if (c.due_date !== null && c.due_date < today) {
          summary.overdueCount += 1;
          summary.pastDueCents += remaining;
        }
      }
    }
  }
  return summary;
}

export function formatCents(cents: number): string {
  // Dollar formatting for US studio; locale-aware version when we
  // internationalize. Negative values render as "-$X.XX".
  const negative = cents < 0;
  const abs = Math.abs(cents);
  return `${negative ? '-' : ''}$${(abs / 100).toFixed(2)}`;
}
